import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import BottomSheet from '../mobile/BottomSheet'
import { CollapsedDisclaimer } from '../compliance/CollapsedDisclaimer'
import BentoCard from '../ui/BentoCard'
import MaterialIcon from '../ui/MaterialIcon'
import MealImage from './MealImage'
import MealSubstituteSheet from './MealSubstituteSheet'
import { UnplannedMealLogger } from './UnplannedMealLogger'
import { KILO } from '../../constants/KILO_THEME'
import type { CycleProfile } from '../../lib/cycle'
import {
  addDaysToIsoDate,
  dayIdForDate,
  getTodayDayId,
  getTodayIsoDate,
  type DayId,
} from '../../lib/days'
import { applyLutealMacroAdjustments, defaultStorageProtocol, getMealsForPrepWindow, scaleGroceryForBatch, type PrepStance } from '../../lib/mealMatrix'
import { buildDailySummaryFromMeals, loadMealLogsForDate, logMeal, mealToLogPayload } from '../../lib/mealLog'
import { buildSlotStatuses, type MealAdherence, type MealLogRow } from '../../lib/mealLogCore'
import { clientTimezone } from '../../lib/clientContext'
import { defaultMealsSelectedDate, filterMealsForCurrentLocalDay } from '../../lib/protocolDayAnchor'
import { localizeIngredientName } from '../../lib/ingredientDisplay'
import type { AppLanguage } from '../../i18n/languages'
import { sanitizeMacroPlan } from '../../lib/macroSanity'
import type { GroceryItem, MealBlock } from '../../lib/protocolTypes'

type MealVisualStatus = 'ate' | 'prepped' | 'planned' | 'skipped'

type DayChip = {
  date: string
  dayId: DayId
  shortName: string
  dateNum: number
  hasLogs: boolean
  isFuture: boolean
}

const MEAL_FILTERS = ['All', 'Breakfast', 'Lunch', 'Snack', 'Dinner'] as const
type MealFilter = (typeof MEAL_FILTERS)[number]

function formatLoggedTime(iso: string): string {
  try {
    return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
  } catch {
    return ''
  }
}

function isPreferenceAdjustedRationale(rationale: string): boolean {
  const lower = rationale.toLowerCase()
  return (
    lower.includes('allergy profile') ||
    lower.includes('dislikes and allergies') ||
    lower.includes('respect your declared') ||
    lower.includes('food preference') ||
    lower.includes('personalised to your taste') ||
    lower.includes('personalized to your taste')
  )
}

function isLutealAdjustedRationale(rationale: string): boolean {
  return rationale.includes('Late-luteal phase') || rationale.includes('metabolic safety buffer')
}

function usePrefersReducedMotion(): boolean {
  const [reduced, setReduced] = useState(false)
  useEffect(() => {
    const mq = window.matchMedia('(prefers-reduced-motion: reduce)')
    setReduced(mq.matches)
    const onChange = () => setReduced(mq.matches)
    mq.addEventListener('change', onChange)
    return () => mq.removeEventListener('change', onChange)
  }, [])
  return reduced
}

function AdherenceRing({ pct, reducedMotion }: { pct: number; reducedMotion: boolean }) {
  const clamped = Math.min(Math.max(pct, 0), 1)
  const transition = reducedMotion ? 'none' : 'stroke-dashoffset 0.9s cubic-bezier(0.4,0,0.2,1)'

  return (
    <div className="relative h-[60px] w-[60px] flex-shrink-0">
      <svg width={60} height={60} viewBox="0 0 60 60" style={{ transform: 'rotate(-90deg)', overflow: 'visible' }}>
        <circle cx={30} cy={30} r={26} fill="none" stroke="var(--kilo-nested)" strokeWidth={5} />
        <circle
          cx={30}
          cy={30}
          r={26}
          fill="none"
          stroke="var(--kilo-accent)"
          strokeWidth={5}
          strokeLinecap="round"
          strokeDasharray={163}
          strokeDashoffset={163 * (1 - clamped)}
          style={{
            filter: 'drop-shadow(0 0 5px rgba(0,200,83,0.45))',
            transition,
          }}
        />
      </svg>
      <div className="absolute inset-0 flex flex-col items-center justify-center">
        <span className={`text-[14px] font-bold leading-none tracking-tight ${KILO.text.primary}`}>
          {Math.round(clamped * 100)}%
        </span>
        <span className={`mt-0.5 text-[8px] font-medium uppercase tracking-[0.4px] ${KILO.text.soft}`}>logged</span>
      </div>
    </div>
  )
}

function deriveMealVisualStatus(intake: MealAdherence, prepared: boolean): MealVisualStatus {
  if (intake === 'on_plan' || intake === 'substituted') return 'ate'
  if (intake === 'skipped') return 'skipped'
  if (prepared) return 'prepped'
  return 'planned'
}

function buildDayChips(todayIso: string, logHintsByDate: Record<string, boolean>): DayChip[] {
  const chips: DayChip[] = []
  for (let offset = -3; offset <= 3; offset++) {
    const date = addDaysToIsoDate(todayIso, offset)
    const d = new Date(date + 'T12:00:00')
    chips.push({
      date,
      dayId: dayIdForDate(d),
      shortName: d.toLocaleDateString(undefined, { weekday: 'short' }),
      dateNum: d.getDate(),
      hasLogs: logHintsByDate[date] ?? false,
      isFuture: date > todayIso,
    })
  }
  return chips
}

type DailyMealLoggerProps = {
  userId: string | null
  meals: MealBlock[]
  grocery: GroceryItem[]
  cycleProfile?: CycleProfile | null
  timezone?: string | null
  onBatchGroceryChange?: (items: GroceryItem[]) => void
}

const DailyMealLogger = ({ userId, meals, grocery, cycleProfile, timezone, onBatchGroceryChange }: DailyMealLoggerProps) => {
  const { t, i18n } = useTranslation()
  const language = (i18n.language.split('-')[0] ?? 'en') as AppLanguage
  const reducedMotion = usePrefersReducedMotion()

  const resolvedTimezone = timezone ?? clientTimezone()
  const todayIso = getTodayIsoDate()
  const todayDayId = getTodayDayId()

  const [selectedDate, setSelectedDate] = useState(() =>
    defaultMealsSelectedDate({ timezone: resolvedTimezone }, meals, todayIso, todayDayId),
  )
  const selectedDayId = useMemo(() => dayIdForDate(new Date(selectedDate + 'T12:00:00')), [selectedDate])
  const logDate = selectedDate

  const [prepStance, setPrepStance] = useState<PrepStance>('DAILY')
  const [prepDaysWindow, setPrepDaysWindow] = useState(1)
  const [activeFilter, setActiveFilter] = useState<MealFilter>('All')
  const [recipeMeal, setRecipeMeal] = useState<MealBlock | null>(null)
  const [logs, setLogs] = useState<MealLogRow[]>([])
  const [logsLoading, setLogsLoading] = useState(false)
  const [logBusy, setLogBusy] = useState<string | null>(null)
  const [logError, setLogError] = useState('')
  const [substituteMeal, setSubstituteMeal] = useState<MealBlock | null>(null)
  const [showFoodLogger, setShowFoodLogger] = useState(false)
  const [logHintsByDate, setLogHintsByDate] = useState<Record<string, boolean>>({})
  const [expandedMealKey, setExpandedMealKey] = useState<string | null>(null)

  const dayChips = useMemo(() => buildDayChips(todayIso, logHintsByDate), [todayIso, logHintsByDate])

  useEffect(() => {
    if (meals.length === 0) return
    const nextDefault = defaultMealsSelectedDate(
      { timezone: resolvedTimezone },
      meals,
      getTodayIsoDate(),
      getTodayDayId(),
    )
    setSelectedDate((prev) => (prev === getTodayIsoDate() ? nextDefault : prev))
  }, [meals, resolvedTimezone])

  const mealsForSelectedDay = useMemo(() => {
    const dayMeals = meals.filter((m) => m.day_id === selectedDayId)
    if (selectedDate !== todayIso) return dayMeals
    return filterMealsForCurrentLocalDay(dayMeals, { timezone: resolvedTimezone })
  }, [meals, selectedDayId, selectedDate, todayIso, resolvedTimezone])

  const dateHeading = useMemo(() => {
    const d = new Date(selectedDate + 'T12:00:00')
    return {
      dayName: t(`meals.weekdays.${selectedDayId}`),
      formattedDate: d.toLocaleDateString(undefined, { day: 'numeric', month: 'long' }),
    }
  }, [selectedDate, selectedDayId, t])

  const windowMeals = useMemo(() => {
    const base =
      prepStance === 'BATCH'
        ? getMealsForPrepWindow(meals, selectedDayId, prepDaysWindow).filter((meal) =>
            selectedDate === todayIso
              ? filterMealsForCurrentLocalDay([meal], { timezone: resolvedTimezone }).length > 0
              : meal.day_id === selectedDayId,
          )
        : mealsForSelectedDay
    return applyLutealMacroAdjustments(base, cycleProfile ?? null)
  }, [meals, selectedDayId, selectedDate, todayIso, resolvedTimezone, prepStance, prepDaysWindow, cycleProfile, mealsForSelectedDay])

  const reloadLogs = useCallback(async () => {
    if (!userId) {
      setLogs([])
      return
    }
    setLogsLoading(true)
    const { logs: loaded, error } = await loadMealLogsForDate(userId, logDate)
    setLogsLoading(false)
    if (error) {
      setLogError(error)
      return
    }
    setLogs(loaded)
    setLogHintsByDate((prev) => ({
      ...prev,
      [logDate]: loaded.some(
        (row) => row.event_type === 'consumed' || row.event_type === 'prepared' || row.event_type === 'skipped',
      ),
    }))
  }, [userId, logDate])

  useEffect(() => {
    void reloadLogs()
  }, [reloadLogs])

  useEffect(() => {
    if (!userId) {
      setLogHintsByDate({})
      return
    }
    let cancelled = false
    const dates = Array.from({ length: 7 }, (_, i) => addDaysToIsoDate(todayIso, i - 3))
    void Promise.all(
      dates.map(async (date) => {
        const { logs: dayLogs } = await loadMealLogsForDate(userId, date)
        return {
          date,
          hasLogs: dayLogs.some(
            (row) => row.event_type === 'consumed' || row.event_type === 'prepared' || row.event_type === 'skipped',
          ),
        }
      }),
    ).then((results) => {
      if (cancelled) return
      setLogHintsByDate((prev) => ({
        ...prev,
        ...Object.fromEntries(results.map((r) => [r.date, r.hasLogs])),
      }))
    })
    return () => {
      cancelled = true
    }
  }, [userId, todayIso])

  useEffect(() => {
    if (!onBatchGroceryChange) return
    const scaled = prepStance === 'BATCH' ? scaleGroceryForBatch(grocery, prepDaysWindow) : grocery
    onBatchGroceryChange(scaled)
  }, [prepStance, prepDaysWindow, grocery, onBatchGroceryChange])

  const activeBlock = activeFilter === 'All' ? null : activeFilter

  const filteredMeals = useMemo(
    () => (activeBlock ? windowMeals.filter((m) => m.block_name === activeBlock) : windowMeals),
    [windowMeals, activeBlock],
  )

  const dayMealsForSummary = useMemo(
    () => mealsForSelectedDay,
    [mealsForSelectedDay],
  )

  const dailySummary = useMemo(
    () => buildDailySummaryFromMeals(logDate, dayMealsForSummary, logs),
    [logDate, dayMealsForSummary, logs],
  )

  const blocks = useMemo(() => [...new Set(windowMeals.map((m) => m.block_name))], [windowMeals])

  const slotStatuses = useMemo(() => buildSlotStatuses(logs, blocks), [logs, blocks])

  const slotStatusFor = (blockName: string) =>
    slotStatuses.find((s) => s.meal_block_name === blockName) ?? {
      meal_block_name: blockName,
      prepared: false,
      intake: 'unlogged' as MealAdherence,
      consumed_entry: null,
      prepared_entries: [],
    }

  const macroTargets = useMemo(() => {
    const raw = {
      protein: dailySummary.planned.protein_g || 180,
      carbs: dailySummary.planned.carbs_g || 220,
      fats: dailySummary.planned.fat_g || 70,
      kcal: dailySummary.planned.calories_kcal || 2400,
    }
    const safe = sanitizeMacroPlan(
      {
        calories_kcal: raw.kcal,
        protein_g: raw.protein,
        carbs_g: raw.carbs,
        fat_g: raw.fats,
      },
      'DailyMealLogger.macroTargets',
    )
    return {
      protein: safe.protein_g,
      carbs: safe.carbs_g,
      fats: safe.fat_g,
      kcal: safe.calories_kcal,
    }
  }, [dailySummary.planned])

  const actualTotals = dailySummary.actual
  const caloriesConsumed = actualTotals.calories_kcal
  const caloriesTarget = macroTargets.kcal
  const adherencePct = caloriesTarget > 0 ? Math.min(caloriesConsumed / caloriesTarget, 1) : 0
  const caloriesRemaining = Math.max(0, caloriesTarget - caloriesConsumed)

  const macros = useMemo(
    () => [
      {
        name: t('meals.macros.protein'),
        consumed: actualTotals.protein_g,
        target: macroTargets.protein,
        colorClass: 'text-kilo-accent',
        fillClass: 'bg-kilo-accent',
      },
      {
        name: t('meals.macros.carbs'),
        consumed: actualTotals.carbs_g,
        target: macroTargets.carbs,
        colorClass: 'text-[#4D9EFF]',
        fillClass: 'bg-[#4D9EFF]',
      },
      {
        name: t('meals.macros.fats'),
        consumed: actualTotals.fat_g,
        target: macroTargets.fats,
        colorClass: 'text-[#FFB300]',
        fillClass: 'bg-[#FFB300]',
      },
    ],
    [actualTotals, macroTargets, t],
  )

  const runLog = async (payload: Parameters<typeof logMeal>[0]) => {
    setLogError('')
    setLogBusy(`${payload.meal_block_name}:${payload.event_type}`)
    const { data, error } = await logMeal(payload)
    setLogBusy(null)
    if (error) {
      setLogError(error)
      return false
    }
    if (data?.entry) {
      setLogs((prev) => {
        const withoutSlot =
          payload.event_type === 'prepared'
            ? prev
            : prev.filter(
                (row) =>
                  !(
                    row.log_date === payload.log_date &&
                    row.meal_block_name === payload.meal_block_name &&
                    (row.event_type === 'consumed' || row.event_type === 'skipped')
                  ),
              )
        return [...withoutSlot, data.entry!]
      })
    } else {
      await reloadLogs()
    }
    return true
  }

  const handleAtePlan = async (meal: MealBlock) => {
    await runLog(mealToLogPayload(meal, logDate, 'consumed'))
  }

  const handlePrepared = async (meal: MealBlock) => {
    await runLog(mealToLogPayload(meal, logDate, 'prepared'))
  }

  const handleSkipped = async (meal: MealBlock) => {
    await runLog({
      log_date: logDate,
      day_id: meal.day_id,
      meal_block_name: meal.block_name,
      meal_protocol_id: meal.id,
      event_type: 'skipped',
      source_type: 'protocol',
      dish_name: meal.dish_name,
    })
  }

  const handleSubstitute = async (
    meal: MealBlock,
    custom: {
      dish_name: string
      calories_kcal: number
      protein_g: number
      carbs_g: number
      fat_g: number
      notes?: string
    },
  ) => {
    const ok = await runLog({
      log_date: logDate,
      day_id: meal.day_id,
      meal_block_name: meal.block_name,
      meal_protocol_id: meal.id,
      event_type: 'consumed',
      source_type: 'custom',
      dish_name: custom.dish_name,
      calories_kcal: custom.calories_kcal,
      protein_g: custom.protein_g,
      carbs_g: custom.carbs_g,
      fat_g: custom.fat_g,
      notes: custom.notes ?? null,
    })
    if (ok) setSubstituteMeal(null)
  }

  const blockLabel = (block: string) => t(`meals.blocks.${block}`, { defaultValue: block })

  const filterLabel = (filter: MealFilter) => {
    if (filter === 'All') return t('meals.filters.all')
    return blockLabel(filter)
  }

  const difficultyLabel = (value: string | null | undefined) => {
    const normalized = String(value ?? 'Easy').toLowerCase()
    if (normalized === 'medium') return t('meals.recipe.difficultyMedium')
    if (normalized === 'hard') return t('meals.recipe.difficultyHard')
    return t('meals.recipe.difficultyEasy')
  }

  if (meals.length === 0) {
    return (
      <BentoCard gradient="none" className="text-center">
        <p className={KILO.meta}>{t('meals.empty.title')}</p>
        <p className={`mt-3 text-sm ${KILO.text.muted}`}>{t('meals.empty.body')}</p>
      </BentoCard>
    )
  }

  return (
    <div className={`flex flex-col pb-24 ${KILO.bg.page}`}>
      {/* Top bar */}
      <div className="flex items-center justify-between px-6 pt-14 pb-0">
        <h1 className={`text-[28px] font-bold tracking-[-0.8px] ${KILO.text.primary}`}>
          {t('common.tabs.meals', { defaultValue: 'Meals' })}
        </h1>
        <button
          type="button"
          onClick={() => setPrepStance((s) => (s === 'BATCH' ? 'DAILY' : 'BATCH'))}
          className={`flex items-center gap-2 rounded-full border px-3 py-[6px] transition-colors ${
            prepStance === 'BATCH'
              ? 'border-[#4D9EFF]/40 bg-[#4D9EFF]/10'
              : `${KILO.bg.card} border-white/[0.07] dark:border-white/[0.07] ${KILO.border.default}`
          }`}
        >
          <div className={`h-1.5 w-1.5 rounded-full ${prepStance === 'BATCH' ? 'bg-[#4D9EFF]' : 'bg-[#4D9EFF]/50'}`} />
          <span className={`text-[13px] tracking-tight ${KILO.text.muted}`}>{t('meals.prep.batch')}</span>
        </button>
      </div>

      {/* Day strip */}
      <div className="scrollbar-hide mt-4 flex gap-1.5 overflow-x-auto px-6">
        {dayChips.map((day) => {
          const selected = day.date === selectedDate
          return (
            <button
              key={day.date}
              type="button"
              onClick={() => setSelectedDate(day.date)}
              className={`flex flex-shrink-0 flex-col items-center gap-[3px] rounded-[10px] px-3 py-2 transition-all duration-150 ${
                selected ? 'bg-kilo-accent' : 'bg-transparent'
              }`}
            >
              <span
                className={`text-[11px] font-semibold uppercase tracking-[0.3px] ${
                  selected ? 'text-black/60' : KILO.text.soft
                }`}
              >
                {day.shortName}
              </span>
              <span className={`text-[17px] font-semibold tracking-tight ${selected ? 'text-black' : KILO.text.muted}`}>
                {day.dateNum}
              </span>
              <div
                className={`h-1 w-1 rounded-full ${
                  day.isFuture
                    ? 'bg-transparent'
                    : day.hasLogs
                      ? selected
                        ? 'bg-black/40'
                        : 'bg-kilo-accent'
                      : 'bg-transparent'
                }`}
              />
            </button>
          )
        })}
      </div>

      {/* Date heading */}
      <div className="mt-5 flex items-baseline gap-2 px-6">
        <span className={`text-[22px] font-bold tracking-tight ${KILO.text.primary}`}>{dateHeading.dayName}</span>
        <span className={`text-[15px] tracking-tight ${KILO.text.soft}`}>{dateHeading.formattedDate}</span>
        {logsLoading ? (
          <span className={`text-[13px] ${KILO.text.soft}`}>{t('meals.header.syncing')}</span>
        ) : null}
      </div>

      {logError ? (
        <div className="mx-6 mt-3 rounded-xl border border-red-900/50 bg-red-950/30 px-3 py-2 text-sm text-red-400">
          {logError}
        </div>
      ) : null}

      {prepStance === 'BATCH' ? (
        <div className={`mx-6 mt-4 rounded-[16px] border p-4 ${KILO.bg.card} ${KILO.border.default}`}>
          <p className={KILO.meta}>{t('meals.prep.window', { days: prepDaysWindow })}</p>
          <input
            type="range"
            min={1}
            max={5}
            value={prepDaysWindow}
            onChange={(e) => setPrepDaysWindow(Number(e.target.value))}
            className={`mt-2 h-1.5 w-full cursor-pointer appearance-none rounded-full ${KILO.progressTrack} accent-kilo-accent`}
          />
        </div>
      ) : null}

      {/* Calorie summary card */}
      <div className={`mx-6 mt-4 rounded-[16px] border p-5 ${KILO.bg.card} ${KILO.border.default}`}>
        <div className="mb-5 flex items-start justify-between">
          <div>
            <div className={`mb-1 text-[11px] font-semibold uppercase tracking-[0.5px] ${KILO.text.soft}`}>
              {t('meals.intake.logged')}
            </div>
            <div className="flex items-baseline gap-1">
              <span className={`text-[48px] font-bold leading-none tracking-[-2px] ${KILO.text.primary}`}>
                {caloriesConsumed.toLocaleString()}
              </span>
              <span className={`text-[18px] font-medium tracking-tight ${KILO.text.muted}`}>kcal</span>
            </div>
            <div className={`mt-1 text-[13px] tracking-tight ${KILO.text.soft}`}>
              <span className={`font-medium ${KILO.text.muted}`}>{caloriesRemaining.toLocaleString()} kcal</span>{' '}
              {t('meals.intake.toTarget', { defaultValue: "to reach today's target" })}
            </div>
          </div>
          <AdherenceRing pct={adherencePct} reducedMotion={reducedMotion} />
        </div>

        <div className="flex">
          {macros.map((macro, i) => (
            <div
              key={macro.name}
              className={`flex flex-1 flex-col gap-[3px] ${i > 0 ? 'ml-3 border-l border-white/[0.07] pl-3 dark:border-white/[0.07]' : ''}`}
            >
              <span className={`text-[16px] font-semibold tracking-tight ${macro.colorClass}`}>
                {Math.round(macro.consumed)}g
              </span>
              <span className={`text-[11px] tracking-[0.1px] ${KILO.text.soft}`}>{macro.name}</span>
              <div className={`mt-0.5 h-[2px] overflow-hidden rounded-full ${KILO.bg.nested}`}>
                <div
                  className={`h-full rounded-full ${macro.fillClass}`}
                  style={{ width: `${macro.target > 0 ? Math.min((macro.consumed / macro.target) * 100, 100) : 0}%` }}
                />
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Log action button */}
      <button
        type="button"
        onClick={() => setShowFoodLogger(true)}
        className="mx-6 mt-3 flex w-[calc(100%-48px)] items-center justify-center gap-2 rounded-[14px]
                   border border-[rgba(0,200,83,0.25)] bg-[rgba(0,200,83,0.14)] py-4
                   transition-colors active:bg-[rgba(0,200,83,0.22)]"
      >
        <div className="flex h-7 w-7 flex-shrink-0 items-center justify-center rounded-[8px] bg-kilo-accent">
          <MaterialIcon name="add" size="sm" className="font-bold text-black" />
        </div>
        <span className="text-[16px] font-semibold tracking-tight text-kilo-accent">
          {t('meals.unplanned.logButton')}
        </span>
      </button>

      {/* Filter chips */}
      <div className="scrollbar-hide mt-5 flex gap-1.5 overflow-x-auto px-6">
        {MEAL_FILTERS.filter((label) => label === 'All' || blocks.includes(label)).map((label) => (
          <button
            key={label}
            type="button"
            onClick={() => setActiveFilter(label)}
            className={`flex-shrink-0 rounded-full border px-3.5 py-[6px] text-[13px] font-medium tracking-tight transition-all duration-150 ${
              activeFilter === label
                ? `${KILO.bg.card} border-white/[0.07] ${KILO.text.primary} dark:border-white/[0.07]`
                : `border-transparent bg-transparent ${KILO.text.soft}`
            }`}
          >
            {filterLabel(label)}
          </button>
        ))}
      </div>

      {/* Meal cards */}
      <div className="mt-3 flex flex-col gap-2 px-6">
        {filteredMeals.map((meal) => {
          const slot = slotStatusFor(meal.block_name)
          const status = deriveMealVisualStatus(slot.intake, slot.prepared)
          const mealKey = `${meal.id}-${meal.day_id}`
          const busyKey = logBusy?.startsWith(`${meal.block_name}:`)
          const expanded = expandedMealKey === mealKey

          const displayName =
            slot.consumed_entry?.source_type === 'custom' ? slot.consumed_entry.dish_name : meal.dish_name
          const macroProtein = slot.consumed_entry?.protein_g ?? meal.protein_g
          const macroCarbs = slot.consumed_entry?.carbs_g ?? meal.carbs_g
          const macroFat = slot.consumed_entry?.fat_g ?? meal.fat_g
          const macroKcal = slot.consumed_entry?.calories_kcal ?? meal.calories_kcal

          const timeLabel =
            status === 'planned'
              ? 'planned'
              : slot.consumed_entry?.logged_at
                ? formatLoggedTime(slot.consumed_entry.logged_at)
                : meal.time_stamp

          const statusBadgeLabel =
            status === 'ate'
              ? t('meals.mealCard.ateThis')
              : status === 'prepped'
                ? t('meals.mealCard.prepped')
                : status === 'planned'
                  ? t('meals.adherence.unlogged', { defaultValue: 'Planned' })
                  : t('meals.mealCard.skipped')

          const rationaleNote = (() => {
            if (slot.consumed_entry?.source_type === 'custom') {
              return (
                <p className={`mt-1 text-xs ${KILO.text.muted}`}>
                  {t('meals.mealCard.planned', { dish: meal.dish_name })}
                </p>
              )
            }
            if (isPreferenceAdjustedRationale(meal.rationale_why)) {
              return (
                <p className="mt-1 text-[10px] italic text-neutral-400 dark:text-neutral-500">
                  {t('meals.mealCard.adjustedForPreferences')}
                </p>
              )
            }
            if (isLutealAdjustedRationale(meal.rationale_why)) {
              return <p className={`mt-1 line-clamp-2 text-sm ${KILO.text.muted}`}>{meal.rationale_why}</p>
            }
            if (meal.rationale_why.trim()) {
              return <p className={`mt-1 line-clamp-2 text-sm ${KILO.text.muted}`}>{meal.rationale_why}</p>
            }
            return null
          })()

          const openRecipe = () => setRecipeMeal(meal)
          const toggleExpanded = () => setExpandedMealKey((k) => (k === mealKey ? null : mealKey))

          return (
            <div
              key={mealKey}
              role="button"
              tabIndex={0}
              onClick={() => {
                if (status === 'ate') openRecipe()
                else if (status === 'planned' || status === 'prepped') toggleExpanded()
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault()
                  if (status === 'ate') openRecipe()
                  else if (status === 'planned' || status === 'prepped') toggleExpanded()
                }
              }}
              className={`cursor-pointer rounded-[14px] border p-4 transition-all ${
                status === 'planned'
                  ? 'border-dashed border-white/10 bg-transparent dark:border-white/10'
                  : status === 'skipped'
                    ? `${KILO.bg.card} border-white/[0.07] opacity-45 dark:border-white/[0.07]`
                    : `${KILO.bg.card} border-white/[0.07] dark:border-white/[0.07]`
              }`}
            >
              <div className="mb-2.5 flex items-start justify-between">
                <div className="min-w-0 flex-1">
                  <div className={`mb-1 text-[11px] font-semibold uppercase tracking-[0.4px] ${KILO.text.soft}`}>
                    {blockLabel(meal.block_name)} · {timeLabel}
                  </div>
                  <div
                    className={`text-[16px] font-semibold leading-tight tracking-tight ${KILO.text.primary} ${
                      status === 'skipped' ? 'line-through decoration-white/30' : ''
                    }`}
                  >
                    {displayName}
                  </div>
                  {rationaleNote}
                </div>
                <div
                  className={`ml-3 mt-0.5 flex-shrink-0 rounded-full px-2.5 py-1 text-[11px] font-semibold tracking-[0.2px] ${
                    status === 'ate'
                      ? 'bg-[rgba(0,200,83,0.14)] text-kilo-accent'
                      : status === 'prepped'
                        ? 'bg-[rgba(77,158,255,0.12)] text-[#4D9EFF]'
                        : status === 'planned'
                          ? `${KILO.bg.nested} ${KILO.text.soft}`
                          : `${KILO.bg.nested} text-white/20`
                  }`}
                >
                  {statusBadgeLabel}
                </div>
              </div>

              {status !== 'skipped' ? (
                <div className="flex flex-wrap gap-1.5">
                  <span className="rounded-[6px] border border-[rgba(0,200,83,0.15)] bg-[rgba(0,200,83,0.10)] px-2.5 py-1 text-[12px] font-medium text-kilo-accent">
                    P {macroProtein}g
                  </span>
                  <span className="rounded-[6px] border border-[rgba(77,158,255,0.15)] bg-[rgba(77,158,255,0.10)] px-2.5 py-1 text-[12px] font-medium text-[#4D9EFF]">
                    C {macroCarbs}g
                  </span>
                  <span className="rounded-[6px] border border-[rgba(255,179,0,0.15)] bg-[rgba(255,179,0,0.10)] px-2.5 py-1 text-[12px] font-medium text-[#FFB300]">
                    F {macroFat}g
                  </span>
                  <span className={`rounded-[6px] border border-white/[0.07] ${KILO.bg.nested} px-2.5 py-1 text-[12px] font-medium ${KILO.text.muted}`}>
                    {macroKcal} kcal
                  </span>
                </div>
              ) : null}

              {(status === 'planned' || status === 'prepped') && expanded ? (
                <div className="mt-3 space-y-2" onClick={(e) => e.stopPropagation()}>
                  {status === 'prepped' ? null : (
                    <button
                      type="button"
                      disabled={Boolean(busyKey) || !userId}
                      onClick={() => void handlePrepared(meal)}
                      className="flex w-full items-center justify-center gap-1 rounded-xl border border-[#4D9EFF]/30 bg-[#4D9EFF]/10 py-2 text-xs font-semibold text-[#4D9EFF] disabled:opacity-50"
                    >
                      <MaterialIcon name="soup_kitchen" size="sm" />
                      {t('meals.mealCard.prepared')}
                    </button>
                  )}
                  <button
                    type="button"
                    disabled={Boolean(busyKey) || !userId}
                    onClick={() => void handleAtePlan(meal)}
                    className="flex w-full items-center justify-center gap-2 rounded-xl border border-kilo-accent/30 bg-kilo-accent/10 py-3 text-sm font-medium text-kilo-accent disabled:opacity-50"
                  >
                    <MaterialIcon name="check_circle" filled size="sm" />
                    {t('meals.mealCard.ateThis')}
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(busyKey) || !userId}
                    onClick={() => setSubstituteMeal(meal)}
                    className={`flex w-full items-center justify-center gap-2 rounded-xl border py-3 text-sm disabled:opacity-50 ${KILO.border.default} ${KILO.text.muted}`}
                  >
                    <MaterialIcon name="swap_horiz" size="sm" />
                    {t('meals.mealCard.swapMeal')}
                  </button>
                  <button
                    type="button"
                    disabled={Boolean(busyKey) || !userId}
                    onClick={() => void handleSkipped(meal)}
                    className="w-full rounded-xl border border-red-900/30 py-2.5 text-center text-sm text-red-400 disabled:opacity-50"
                  >
                    {t('meals.mealCard.skipMeal')}
                  </button>
                  <button
                    type="button"
                    className={`${KILO.btnSecondary} hover:text-kilo-accent`}
                    onClick={openRecipe}
                  >
                    {t('meals.mealCard.viewRecipe')}
                    <MaterialIcon name="chevron_right" hoverable size="sm" />
                  </button>
                </div>
              ) : null}

              {status === 'ate' ? (
                <button
                  type="button"
                  className={`group/btn mt-3 ${KILO.btnSecondary} hover:text-kilo-accent`}
                  onClick={(e) => {
                    e.stopPropagation()
                    openRecipe()
                  }}
                >
                  {t('meals.mealCard.viewRecipe')}
                  <MaterialIcon name="chevron_right" hoverable size="sm" />
                </button>
              ) : null}
            </div>
          )
        })}
      </div>

      <CollapsedDisclaimer />

      <MealSubstituteSheet
        open={Boolean(substituteMeal)}
        mealBlockName={substituteMeal ? blockLabel(substituteMeal.block_name) : ''}
        busy={Boolean(logBusy)}
        onClose={() => setSubstituteMeal(null)}
        onSubmit={async (custom) => {
          if (!substituteMeal) return
          await handleSubstitute(substituteMeal, custom)
        }}
      />

      <UnplannedMealLogger
        isOpen={showFoodLogger}
        onClose={() => setShowFoodLogger(false)}
        onLogged={() => void reloadLogs()}
      />

      <BottomSheet
        variant="dark"
        open={Boolean(recipeMeal)}
        onClose={() => setRecipeMeal(null)}
        title={recipeMeal?.dish_name ?? t('meals.recipe.title')}
      >
        {recipeMeal ? (
          <div className={`flex flex-col gap-4 text-sm ${KILO.text.muted}`}>
            <MealImage
              mealName={recipeMeal.dish_name}
              mealDescription={recipeMeal.visual_description ?? recipeMeal.rationale_why}
              imageUrl={recipeMeal.image_url}
              ingredients={recipeMeal.ingredients}
              className="h-48 w-full"
            />

            {recipeMeal.visual_description ? (
              <p className={`rounded-xl border border-kilo-line bg-kilo-nested p-3 italic ${KILO.text.soft}`}>
                &quot;{recipeMeal.visual_description}&quot;
              </p>
            ) : null}

            <div>
              <p className={KILO.meta}>{t('meals.recipe.ingredients')}</p>
              <ul className="mt-2 space-y-1.5">
                {recipeMeal.ingredients.map((ing) => (
                  <li key={ing.name} className={`flex justify-between gap-2 rounded-lg bg-kilo-nested px-3 py-2`}>
                    <span className={KILO.text.primary}>{localizeIngredientName(ing.name, language)}</span>
                    <span className={`font-mono ${KILO.text.muted}`}>{ing.amount}</span>
                  </li>
                ))}
              </ul>
            </div>

            {recipeMeal.cooking_instructions?.length ? (
              <div>
                <p className={KILO.meta}>{t('meals.recipe.protocol')}</p>
                <ol className="mt-2 space-y-2">
                  {recipeMeal.cooking_instructions.map((step) => (
                    <li key={step.step} className={`flex gap-3 rounded-lg border border-kilo-line ${KILO.bg.card} p-3`}>
                      <span className="font-mono text-xs text-kilo-accent">{String(step.step).padStart(2, '0')}</span>
                      <span>{step.instruction}</span>
                    </li>
                  ))}
                </ol>
              </div>
            ) : null}

            {prepStance === 'BATCH' ? (
              <div className="rounded-xl border border-amber-900/40 bg-amber-950/20 p-4 text-amber-400">
                <div className="flex items-center gap-2">
                  <MaterialIcon name="warning" size="sm" />
                  <p className="font-mono text-[10px] uppercase tracking-[0.2em]">
                    {(recipeMeal.storage_protocol ?? defaultStorageProtocol(recipeMeal.dish_name)).title}
                  </p>
                </div>
                <ul className="mt-2 list-disc space-y-1 pl-5 text-sm">
                  {(recipeMeal.storage_protocol ?? defaultStorageProtocol(recipeMeal.dish_name)).rules.map((rule) => (
                    <li key={rule}>{rule}</li>
                  ))}
                </ul>
              </div>
            ) : null}

            <p className={`flex items-center gap-2 font-mono text-xs ${KILO.text.muted}`}>
              <MaterialIcon name="schedule" size="sm" />
              {t('meals.recipe.duration', {
                minutes: recipeMeal.cooking_time_minutes ?? '—',
                difficulty: difficultyLabel(recipeMeal.difficulty),
              })}
            </p>
          </div>
        ) : null}
      </BottomSheet>
    </div>
  )
}

export default DailyMealLogger
